import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UploadInvestableUniverseDialog } from "@/components/benchmark/UploadInvestableUniverseDialog";

type UniverseRow = {
  id: string;
  source: string;
  fund_manager: string | null;
  fund_name: string;
  citicode: string | null;
  sedol: string | null;
  isin: string | null;
  currency: string | null;
  status: string | null;
  amc: number | null;
  ocf: number | null;
};

export default function InvestableUniverse() {
  const [query, setQuery] = useState("");
  const { hasRole, staffMember } = useAuth();
  // Investment team members manage the universe day to day; DB RLS already grants them writes.
  const canManage = hasRole(["admin", "superadmin"]) || staffMember?.team === "Investment";
  const queryClient = useQueryClient();

  const { data: lastUpdated } = useQuery({
    queryKey: ["investable_universe_last_updated"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("investable_universe")
        .select("updated_at")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.updated_at ?? null;
    },
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["investable_universe_all"],
    queryFn: async () => {
      const all: UniverseRow[] = [];
      let from = 0;
      const size = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("investable_universe")
          .select("id, source, fund_manager, fund_name, citicode, sedol, isin, currency, status, amc, ocf")
          .or("status.is.null,status.not.ilike.%sell%tradeable%")
          .order("fund_name", { ascending: true })
          .range(from, from + size - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all.push(...(data as UniverseRow[]));
        if (data.length < size) break;
        from += size;
      }
      return all;
    },
  });

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return null;
    const d = new Date(lastUpdated);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }, [lastUpdated]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows.slice(0, 200);
    return rows
      .filter((r) =>
        [r.fund_name, r.fund_manager, r.isin, r.citicode, r.sedol]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
      .slice(0, 500);
  }, [rows, query]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Investable Universe</h1>
          <p className="text-sm text-muted-foreground">
            Search Abrdn Wrap ETI &amp; DFM Hub mutual funds by ISIN, citicode, SEDOL, fund name or manager.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdatedLabel && (
            <span className="text-xs text-muted-foreground">
              Last updated {lastUpdatedLabel}
            </span>
          )}
          {rows && (
            <Badge variant="secondary">{rows.length.toLocaleString()} instruments</Badge>
          )}
          {canManage && (
            <UploadInvestableUniverseDialog
              onSuccess={() =>
                queryClient.invalidateQueries({ queryKey: ["investable_universe_all"] })
              }
            />
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Type ISIN, citicode, SEDOL, fund name or fund manager…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading universe…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">No matches</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Fund Manager</th>
                    <th className="text-left px-3 py-2">Fund Name</th>
                    <th className="text-left px-3 py-2">ISIN</th>
                    <th className="text-left px-3 py-2">Citicode</th>
                    <th className="text-left px-3 py-2">SEDOL</th>
                    <th className="text-left px-3 py-2">Ccy</th>
                    <th className="text-right px-3 py-2">OCF %</th>
                    <th className="text-left px-3 py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-2">{r.fund_manager ?? "—"}</td>
                      <td className="px-3 py-2 font-medium">{r.fund_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.isin ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.citicode ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.sedol ?? "—"}</td>
                      <td className="px-3 py-2">{r.currency ?? "—"}</td>
                      <td className="px-3 py-2 text-right">{r.ocf != null ? Number(r.ocf).toFixed(2) : "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">{r.source}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!query && rows && rows.length > 200 && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-t">
                  Showing first 200 — start typing to search all {rows.length.toLocaleString()} instruments.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
